import { useCallback } from "react";
import { useRouter } from "expo-router";
import { useAuthContext } from "./use-auth-context";

/**
 * Returns a guard that runs `action` when the user is logged in, or otherwise
 * sends them to login while remembering where to return afterwards.
 *
 * The return target travels as a `?redirect=` URL param so it survives the
 * pre-auth hops (login → signup) without a module-scoped store.
 *
 * @example
 * const requireAuth = useRequireAuth();
 * requireAuth(`/event/${eventId}`, () => joinEvent());
 */
export function useRequireAuth() {
  const { isLoggedIn } = useAuthContext();
  const router = useRouter();

  return useCallback(
    (redirectTo: string, action: () => void) => {
      if (isLoggedIn) {
        action();
        return;
      }
      router.push(`/login?redirect=${encodeURIComponent(redirectTo)}`);
    },
    [isLoggedIn, router]
  );
}
